import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Button, MenuItem } from '@material-ui/core';
import _ from 'lodash';

import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { AUTO_COMPLETE_DEFAULT, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useUser } from '../../../stores/SessionStore';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService } from '../../../services/FormSaveService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import NgpTextField from '../../form/NgpTextField';
import NgpSelect from '../../form/NgpSelect';
import { AdminPages, buildPageId } from '../AdminLinkUtils';
import { NAME_FIELD, TOKEN_FIELD, TOKEN_ROLE_FIELD } from './ApiTokenConstants';
import {
    ADMIN_ONLY_ROLES_KEY_VALUE,
    ALL_ROLES_KEY_VALUE,
    ROLE_NAMES,
    SYSTEM_ADMIN_ROLE,
    USER_ROLE_FIELD,
} from '../users/UserConstants';
import useUiAlerts, { createAlertConfig, INFO } from '../../../stores/AlertsStore';
import { ID_FIELD } from '../../form/FormConstants';
import NgpCopyToClipboard from '../../form/NgpCopyToClipboard';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import { useNavigate } from '../../../utilities/PgmUtilities';

const functionName = AdminPages.ApiTokens;
const goToUrl = '..';
const baseUrl = '/ngp/v2/apitokens';
const defaultValues = {
    [NAME_FIELD]: '',
    [TOKEN_ROLE_FIELD]: '',
};

const saveButtonLabel = i18n._(t`Generate`);
const doneButtonLabel = i18n._(t`Done`);

export default function ApiTokensDetail() {
    const formSaveService = useFormSaveService();
    const navigate = useNavigate();
    const [, uiAlertActions] = useUiAlerts();
    const methods = useForm(
        _.extend(USE_FORM_DEFAULT_OPTIONS, {
            defaultValues,
        })
    );
    const { setError, handleSubmit } = methods;
    const functionBreadCrumbs = [{ link: goToUrl, text: i18n._(t`API Tokens`) }];
    const newBreadCrumbText = t`New API Token`;
    const [loggedInUser] = useUser();
    const [recordId, setRecordId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tokenData, setTokenData] = useState(null);

    const userRoleOptions =
        loggedInUser[USER_ROLE_FIELD] === SYSTEM_ADMIN_ROLE ? ALL_ROLES_KEY_VALUE : ADMIN_ONLY_ROLES_KEY_VALUE;

    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, tokenData, NAME_FIELD, newBreadCrumbText);

    const handleFormSubmit = (dataToSave = {}) => {
        setSaving(true);
        return formSaveService
            .save({
                url: baseUrl,
                recordId,
                dataToSave,
                setError,
            })
            .then((response) => {
                uiAlertActions.createAlert(
                    createAlertConfig(INFO, i18n._(t`This is the only time this token can be viewed/copied.`))
                );

                setRecordId(_.get(response || {}, ID_FIELD));
                setTokenData(response);
                return response;
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const handleSubmitFunction = handleFormSubmit;

    const handleCancel = () => {
        return navigate(goToUrl, { replace: true });
    };

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {!_.isEmpty(tokenData) ? (
                        <Button onClick={handleCancel} variant="contained" color="primary">
                            {doneButtonLabel}
                        </Button>
                    ) : (
                        <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                            {saveButtonLabel}
                        </Button>
                    )}
                </Actions>
            }
        >
            <FormProvider {...methods}>
                {!_.isEmpty(tokenData) && (
                    <NgpForm
                        submitFunction={handleCancel}
                        initialized
                        loading={saving}
                        saveButtonLabel={doneButtonLabel}
                    >
                        <FormSection title={<Trans>API Token Detail</Trans>}>
                            <NgpLabelValue dataLabel={<Trans>Name</Trans>} dataValue={tokenData[NAME_FIELD]} />
                            <NgpLabelValue
                                dataLabel={<Trans>Role</Trans>}
                                dataValue={ROLE_NAMES[tokenData[TOKEN_ROLE_FIELD]]}
                            />

                            <NgpCopyToClipboard
                                label={<Trans>Token</Trans>}
                                inputProps={{ size: 50 }}
                                value={tokenData[TOKEN_FIELD]}
                            />
                        </FormSection>
                    </NgpForm>
                )}

                {_.isEmpty(tokenData) && (
                    <NgpForm
                        submitFunction={handleSubmitFunction}
                        initialized
                        loading={saving}
                        saveButtonLabel={saveButtonLabel}
                    >
                        <FormSection title={<Trans>API Token Detail</Trans>}>
                            <NgpTextField
                                fieldName={NAME_FIELD}
                                label={<Trans>Name</Trans>}
                                required
                                inputProps={{
                                    size: 50,
                                    maxLength: 50,
                                    autoComplete: AUTO_COMPLETE_DEFAULT,
                                }}
                            />

                            <NgpSelect fieldName={TOKEN_ROLE_FIELD} label={<Trans>Role</Trans>} required>
                                {_.map(userRoleOptions, (value, key) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                        </FormSection>
                    </NgpForm>
                )}
            </FormProvider>
        </AdminPage>
    );
}
